import {
	ColorPicker,
	FormItem,
	InputWithMoreInfo,
	ValidationError,
	DimensionRecommendationKind,
	useAlertDialog
} from "client/Components";
import { ColorContrastRequirements } from "client/Components/ColorPicker/BaseColorPicker";
import { IValueInputAttachable } from "client/Forms/attachable";
import { ReactElement, useCallback } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { BrandingImageConstraints, DarkThemeSettingsSaveModel } from "client/Api/Branding/branding";
import { FileInput } from "./FileInput";
import styles from "./ThemesTab.module.less";

export type DarkThemeProps = {
	attachable: IValueInputAttachable<DarkThemeSettingsSaveModel>;
	logoConstraints: BrandingImageConstraints;
	primaryColorContrastRequirements: ColorContrastRequirements;
	darkThemeWorkspaceLogoId: string | null;
	darkThemePublicPageLogoId: string | null;
	darkThemeHomeLogoId: string | null;
};

export const DarkTheme = (props: DarkThemeProps): ReactElement => {
	const {
		attachable,
		logoConstraints,
		primaryColorContrastRequirements,
		darkThemeHomeLogoId,
		darkThemePublicPageLogoId,
		darkThemeWorkspaceLogoId
	} = props;

	const [alertDialog, openAlertDialog] = useAlertDialog();

	const showPrimaryColorContrastInfoDialogHandler = useCallback(
		(): void => {
			openAlertDialog({
				title: localize("_PrimaryColorForDarkTheme"),
				children: localize("_TheSelectedPrimaryColorMustMeetTheAccessibilityStandardsInfo"),
				showCloseButton: true,
				trackingId: "dt-primary-color-info-dialog"
			});
		},
		[openAlertDialog]
	);

	const showLinkColorContrastInfoDialogHandler = useCallback(
		(): void => {
			openAlertDialog({
				title: localize("_TextLinkColorForDarkTheme"),
				children: localize("_TheSelectedTextLinkColorMustMeetTheAccessibilityStandardsInfo"),
				showCloseButton: true,
				trackingId: "dt-text-link-color-info-dialog"
			});
		},
		[openAlertDialog]
	);

	return (
		<>
			<section className={styles.section}>
				<h3 className={styles.header}>{localize("_DarkTheme")}</h3>
				<FormItem
					label={localize("_PrimaryColor")}
				>
					<InputWithMoreInfo
						onMoreInfo={showPrimaryColorContrastInfoDialogHandler}
					>
						<ColorPicker
							{...attachable.for("primaryColor")
								.attachToColorPicker()
							}
							contrastRequirements={primaryColorContrastRequirements}
							menuTitle={localize("_PrimaryColor")}
						/>
					</InputWithMoreInfo>
					<ValidationError
						{...attachable.for("primaryColor")
							.attachToValidationError()
						}
					/>
				</FormItem>
				<FormItem
					label={localize("_TextLinkColor")}
				>
					<InputWithMoreInfo
						onMoreInfo={showLinkColorContrastInfoDialogHandler}
					>
						<ColorPicker
							{...attachable.for("textLinkColor")
								.attachToColorPicker()
							}
							contrastRequirements={primaryColorContrastRequirements}
							menuTitle={localize("_TextLinkColor")}
						/>
					</InputWithMoreInfo>
					<ValidationError
						{...attachable.for("textLinkColor")
							.attachToValidationError()
						}
					/>
				</FormItem>
				<FileInput
					label={localize("_WorkspaceLogo")}
					attachable={attachable.for("workspaceLogo")}
					originalImageId={darkThemeWorkspaceLogoId}
					supportedImageTypes={logoConstraints.supportedImageTypes}
					supportedFileDetails={logoConstraints}
					dimensionRecomendation={{
						kind: DimensionRecommendationKind.Height,
						height: 50
					}}
					supportedExtensions={[localize("_FileExtensionJpg"), localize("_FileExtensionPng")]}
				/>
				<FileInput
					label={localize("_PublicPageLogo")}
					attachable={attachable.for("publicPageLogo")}
					originalImageId={darkThemePublicPageLogoId}
					supportedImageTypes={logoConstraints.supportedImageTypes}
					supportedFileDetails={logoConstraints}
					dimensionRecomendation={{
						kind: DimensionRecommendationKind.Height,
						height: 50
					}}
					supportedExtensions={[localize("_FileExtensionJpg"), localize("_FileExtensionPng")]}
				/>
				<FileInput
					label={localize("_HomeLogo")}
					attachable={attachable.for("homeLogo")}
					originalImageId={darkThemeHomeLogoId}
					supportedImageTypes={logoConstraints.supportedImageTypes}
					supportedFileDetails={logoConstraints}
					dimensionRecomendation={{
						kind: DimensionRecommendationKind.Height,
						height: 32
					}}
					supportedExtensions={[localize("_FileExtensionJpg"), localize("_FileExtensionPng")]}
				/>
			</section>
			{alertDialog}
		</>
	);
};
DarkTheme.displayName = "DarkTheme";